<template>
  <div class="app-container">
    <div class="page-title">
      <h2>{{ $t('controleInterne.etapes.title') }}</h2>
    </div>

    <el-card class="box-card">
      <div class="filter-container">
        <el-input
          v-model="query.keyword"
          :placeholder="$t('general.keyword')"
          class="wd-200 mr-15 filter-item"
          @keyup.enter.native="handleFilter"
        />
        <el-button v-waves class="mr-15 filter-item" type="primary" icon="el-icon-search" @click="handleFilter">
          {{ $t('general.search') }}
        </el-button>
        <el-checkbox v-model="query.all" class="mr-15 filter-item" @change="getList">
          {{ $t('controleInterne.etapes.showHidden') }}
        </el-checkbox>
        <el-button
          v-if="checkPermission(['gerer questions'])"
          class="mr-15 filter-item"
          type="primary"
          icon="el-icon-plus"
          @click="handleCreate"
        >
          {{ $t('general.add') }}
        </el-button>
      </div>

      <el-table v-loading="loading" :data="filteredList" border fit highlight-current-row style="width: 100%">
        <el-table-column align="center" :label="$t('controleInterne.etapes.ordre')" width="80">
          <template slot-scope="scope">
            <span>{{ scope.row.ordre }}</span>
          </template>
        </el-table-column>

        <el-table-column align="center" :label="$t('general.code')" min-width="120">
          <template slot-scope="scope">
            <span>{{ scope.row.code }}</span>
          </template>
        </el-table-column>

        <el-table-column align="center" :label="$t('general.libelle')" min-width="220">
          <template slot-scope="scope">
            <span>{{ scope.row.libelle }}</span>
          </template>
        </el-table-column>

        <el-table-column align="center" :label="$t('controleInterne.etapes.required')" width="100">
          <template slot-scope="scope">
            <el-tag :type="scope.row.is_required ? 'success' : 'info'" size="mini">
              {{ scope.row.is_required ? $t('general.yes') : $t('general.no') }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column align="center" :label="$t('controleInterne.etapes.questionsCount')" width="110">
          <template slot-scope="scope">
            <span>{{ (scope.row.questions || []).length }}</span>
          </template>
        </el-table-column>

        <el-table-column align="center" :label="$t('general.status')" width="100">
          <template slot-scope="scope">
            <el-tag :type="scope.row.is_active ? 'success' : 'danger'" size="mini">
              {{ scope.row.is_active ? $t('general.M_actif') : $t('general.M_desactives') }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column
          v-if="checkPermission(['gerer questions'])"
          align="center"
          :label="$t('general.actions')"
          width="160"
        >
          <template slot-scope="scope">
            <el-button type="primary" size="small" icon="el-icon-edit" @click="handleEdit(scope.row)" />
            <el-button
              v-if="scope.row.is_visible"
              type="danger"
              size="small"
              icon="el-icon-delete"
              @click="handleDelete(scope.row)"
            />
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <el-dialog
      :title="editing ? $t('controleInterne.etapes.edit') : $t('controleInterne.etapes.add')"
      :visible.sync="dialogVisible"
      width="50%"
    >
      <el-form ref="etapeForm" :model="currentEtape" :rules="rules" label-position="top">
        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item :label="$t('controleInterne.etapes.ordre')" prop="ordre">
              <el-input-number v-model="currentEtape.ordre" :min="1" style="width: 100%;" />
            </el-form-item>
          </el-col>
          <el-col :span="16">
            <el-form-item :label="$t('general.code')" prop="code">
              <el-input v-model="currentEtape.code" />
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item :label="$t('general.libelle')" prop="libelle">
          <el-input v-model="currentEtape.libelle" />
        </el-form-item>

        <el-form-item :label="$t('general.description')">
          <el-input v-model="currentEtape.description" type="textarea" :rows="2" />
        </el-form-item>

        <el-form-item :label="$t('categorieQuestion.categorieQuestion')">
          <el-select v-model="currentEtape.categorie_question_id" clearable filterable style="width: 100%;">
            <el-option
              v-for="cat in ciCategories"
              :key="cat.id"
              :label="cat.libelle"
              :value="cat.id"
            />
          </el-select>
        </el-form-item>

        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item :label="$t('controleInterne.etapes.required')">
              <el-switch v-model="currentEtape.is_required" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item :label="$t('general.M_actif')">
              <el-switch v-model="currentEtape.is_active" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item :label="$t('general.visible')">
              <el-switch v-model="currentEtape.is_visible" />
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item :label="$t('controleInterne.etapes.linkedQuestions')">
          <el-select
            v-model="selectedQuestionIds"
            multiple
            filterable
            style="width: 100%;"
            :placeholder="$t('controleInterne.etapes.selectQuestions')"
          >
            <el-option
              v-for="question in ciQuestions"
              :key="question.id"
              :label="formatQuestionLabel(question)"
              :value="question.id"
            />
          </el-select>
        </el-form-item>
      </el-form>

      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">{{ $t('general.cancel') }}</el-button>
        <el-button type="primary" :loading="saving" @click="saveEtape">
          {{ saving ? $t('general.saving') : $t('general.save') }}
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import Resource from '@/api/resource';
import waves from '@/directive/waves';
import checkPermission from '@/utils/permission';

const etapeResource = new Resource('controle_interne_etape_configs');
const categorieQuestionResource = new Resource('categorie_questions');
const questionResource = new Resource('questions');

export default {
  name: 'ControleInterneEtapesList',
  directives: { waves },
  data() {
    return {
      list: [],
      loading: false,
      saving: false,
      editing: false,
      dialogVisible: false,
      ciCategories: [],
      ciQuestions: [],
      selectedQuestionIds: [],
      query: {
        keyword: '',
        all: false,
      },
      currentEtape: {},
      rules: {
        libelle: [{ required: true, message: this.$t('controleInterne.etapes.libelleRequired'), trigger: 'blur' }],
        ordre: [{ required: true, message: this.$t('controleInterne.etapes.ordreRequired'), trigger: 'change' }],
      },
    };
  },
  computed: {
    filteredList() {
      if (!this.query.keyword) {
        return this.list;
      }
      const keyword = this.query.keyword.toLowerCase();
      return this.list.filter(item => {
        return (item.libelle || '').toLowerCase().includes(keyword) ||
          (item.code || '').toLowerCase().includes(keyword);
      });
    },
  },
  created() {
    this.getList();
    this.loadCiReferences();
  },
  methods: {
    checkPermission,
    formatQuestionLabel(question) {
      const numero = question.numero ? `${question.numero} — ` : '';
      return `${numero}${question.libelle}`;
    },
    resetEtape() {
      this.currentEtape = {
        code: '',
        libelle: '',
        description: '',
        ordre: this.list.length + 1,
        categorie_question_id: null,
        is_required: true,
        is_active: true,
        is_visible: true,
      };
      this.selectedQuestionIds = [];
    },
    async loadCiReferences() {
      const { data: categories } = await categorieQuestionResource.list({ limit: 500 });
      this.ciCategories = (categories || []).filter(cat => cat.ci === true || cat.ci === 1);
      const { data: questions } = await questionResource.list({ limit: 1000 });
      const ciCategoryIds = this.ciCategories.map(cat => cat.id);
      this.ciQuestions = (questions || []).filter(q => ciCategoryIds.includes(q.categorie_question_id));
    },
    async getList() {
      this.loading = true;
      try {
        const { data } = await etapeResource.list({ all: this.query.all ? 1 : 0 });
        this.list = data || [];
      } finally {
        this.loading = false;
      }
    },
    handleFilter() {
      this.getList();
    },
    handleCreate() {
      this.editing = false;
      this.resetEtape();
      this.dialogVisible = true;
      this.$nextTick(() => this.$refs.etapeForm && this.$refs.etapeForm.clearValidate());
    },
    handleEdit(row) {
      this.editing = true;
      this.currentEtape = {
        id: row.id,
        code: row.code,
        libelle: row.libelle,
        description: row.description,
        ordre: row.ordre,
        categorie_question_id: row.categorie_question_id,
        is_required: row.is_required,
        is_active: row.is_active,
        is_visible: row.is_visible,
      };
      this.selectedQuestionIds = (row.questions || []).map(q => q.id);
      this.dialogVisible = true;
      this.$nextTick(() => this.$refs.etapeForm && this.$refs.etapeForm.clearValidate());
    },
    buildPayload() {
      return {
        ...this.currentEtape,
        questions: this.selectedQuestionIds.map((id, index) => ({
          id,
          ordre: index + 1,
          is_required: true,
        })),
      };
    },
    saveEtape() {
      this.$refs.etapeForm.validate(async valid => {
        if (!valid) {
          return;
        }
        this.saving = true;
        try {
          const payload = this.buildPayload();
          if (this.editing) {
            await etapeResource.update(this.currentEtape.id, payload);
            this.$message.success(this.$t('controleInterne.etapes.updated'));
          } else {
            await etapeResource.store(payload);
            this.$message.success(this.$t('controleInterne.etapes.created'));
          }
          this.dialogVisible = false;
          this.getList();
        } catch (error) {
          console.error(error);
          this.$message.error(this.$t('general.errorOccurred'));
        } finally {
          this.saving = false;
        }
      });
    },
    handleDelete(row) {
      this.$confirm(this.$t('controleInterne.etapes.deleteWarn') + ' « ' + row.libelle + ' ». ' + this.$t('general.deleteContinue'), 'Warning', {
        type: 'warning',
      }).then(async() => {
        await etapeResource.destroy(row.id);
        this.$message.success(this.$t('controleInterne.etapes.deleted'));
        this.getList();
      }).catch(() => {});
    },
  },
};
</script>
